import { Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { OAuthCallbackPage } from "./pages/OAuthCallbackPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { ProjectListPage } from "./pages/ProjectListPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { ProjectCreatePage } from "./pages/ProjectCreatePage";
import { ProjectEditPage } from "./pages/ProjectEditPage";
import { InspectionPage } from "./pages/InspectionPage";
import { SyncPage } from "./pages/SyncPage";
import { SettingsPage } from "./pages/SettingsPage";
import { Layout } from "./components/layout/Layout";
import { useAuth } from "./hooks/useAuth";
import { useDeepLink } from "./hooks/useDeepLink";
import { FullScreenSpinner } from "./components/ui/FullScreenSpinner";

/**
 * 인증만 체크하는 라우트 가드
 * companyId는 확인하지 않음 (온보딩 페이지에서 사용)
 */
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="safe-area-top safe-area-bottom">
        <FullScreenSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * 인증 + 회사 연결 확인 라우트 가드
 * companyId가 없으면 온보딩 페이지로 리다이렉트
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="safe-area-top safe-area-bottom">
        <FullScreenSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 회사 미연결 사용자는 온보딩으로 안내
  if (!user?.companyId) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  // 딥링크 이벤트 핸들러 등록 (OAuth 콜백 처리)
  useDeepLink();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />

      {/* 온보딩: 인증만 필요, 회사 연결 불필요 (독립 페이지) */}
      <Route
        path="/onboarding"
        element={
          <AuthRoute>
            <OnboardingPage />
          </AuthRoute>
        }
      />

      {/* 메인 헤더가 표시되는 레이아웃 (ProjectListPage) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout showHeader={true} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/projects" replace />} />
        <Route path="projects" element={<ProjectListPage />} />
      </Route>

      {/* 메인 헤더가 숨겨지는 레이아웃 (서브 헤더가 있는 페이지들) */}
      <Route
        element={
          <ProtectedRoute>
            <Layout showHeader={false} />
          </ProtectedRoute>
        }
      >
        <Route path="projects/new" element={<ProjectCreatePage />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="projects/:id/edit" element={<ProjectEditPage />} />
        <Route
          path="projects/:projectId/inspection/:docKey/:machineId"
          element={<InspectionPage />}
        />
        <Route path="sync" element={<SyncPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}
